import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Layout, PageHero, CTA } from '@components';
import { person, email } from '@config';
import FeatureIcon from '@components/icons/feature';

const StyledSection = styled.section`
  ${({ theme }) => theme.mixins.section};
  background: var(--paper);

  &:nth-of-type(even) {
    background: var(--paper-soft);
    border-top: 1px solid var(--line);
    border-bottom: 1px solid var(--line);
  }
`;

const StyledInner = styled.div`
  ${({ theme }) => theme.mixins.container};
`;

const StyledBio = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 56px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  p {
    color: var(--text-soft);
    font-size: var(--fz-lg);
  }
`;

const StyledProfile = styled.div`
  position: relative;
  overflow: hidden;
  padding: 36px 32px;
  border-radius: var(--radius-xl);
  background: var(--ink-900);
  background-image: radial-gradient(420px 260px at 85% 0%, rgba(86, 112, 246, 0.4), transparent 60%),
    radial-gradient(360px 260px at 0% 100%, rgba(253, 118, 255, 0.22), transparent 60%);
  color: var(--on-dark-soft);

  .avatar {
    ${({ theme }) => theme.mixins.flexCenter};
    width: 84px;
    height: 84px;
    margin-bottom: 22px;
    border-radius: 50%;
    background: var(--gradient);
    color: #ffffff;
    font-size: 28px;
    font-weight: 700;
  }

  h3 {
    margin: 0 0 4px;
    color: #ffffff;
    font-size: var(--fz-xxl);
  }

  .academic {
    margin: 0 0 22px;
    color: var(--blue-soft);
    font-size: var(--fz-sm);
  }

  dl {
    margin: 0;

    div {
      padding: 14px 0;
      border-top: 1px solid var(--on-dark-line);
    }

    dt {
      margin-bottom: 4px;
      color: var(--on-dark-mute);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }

    dd {
      margin: 0;
      color: #ffffff;
      font-size: var(--fz-sm);
      line-height: 1.5;
    }
  }
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  margin-top: 44px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const StyledItem = styled.div`
  padding: 26px 24px;
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
  background: var(--paper);

  .icon {
    ${({ theme }) => theme.mixins.flexCenter};
    width: 46px;
    height: 46px;
    margin-bottom: 16px;
    border-radius: 13px;
    color: var(--blue);
    background: rgba(86, 112, 246, 0.12);
  }

  h3 {
    margin: 0 0 6px;
    font-size: var(--fz-md);
  }

  p {
    margin: 0;
    color: var(--text-soft);
    font-size: var(--fz-sm);
    line-height: 1.55;
  }
`;

const specialization = [
  {
    icon: 'kanban',
    title: 'Управление IT-проектами',
    text: 'Планирование, организация и сопровождение разработки программных продуктов.',
  },
  {
    icon: 'code',
    title: 'Разработка ПО',
    text: 'Проектирование и разработка программного обеспечения под конкретные задачи.',
  },
  {
    icon: 'cloud',
    title: 'Создание сайтов',
    text: 'Современные адаптивные сайты, лендинги и корпоративные веб-решения.',
  },
  {
    icon: 'phone',
    title: 'Мобильные приложения',
    text: 'Разработка приложений для Android и других платформ с современным интерфейсом.',
  },
  {
    icon: 'flow',
    title: 'Веб-сервисы',
    text: 'Проектирование и разработка цифровых сервисов и их серверной части.',
  },
  {
    icon: 'ai',
    title: 'Искусственный интеллект',
    text: 'Применение AI в разработке и управлении IT-проектами.',
  },
  {
    icon: 'shield',
    title: 'Защита информации',
    text: 'Учёт требований информационной безопасности при проектировании решений.',
  },
  {
    icon: 'experience',
    title: 'Сопровождение проектов',
    text: 'Организация и сопровождение IT-проектов на всех этапах.',
  },
];

const AboutPage = ({ location }) => (
  <Layout
    location={location}
    title="О специалисте — Пушкарева Мария Викторовна"
    description="Пушкарева Мария Викторовна — кандидат экономических наук, доцент ЮУрГУ, специалист по разработке ПО и управлению IT-проектами.">
    <main>
      <PageHero
        eyebrow="О специалисте"
        title={person.name}
        lead={`${person.role}. Кандидат экономических наук, доцент кафедры «Системное программирование» Института естественных и точных наук ЮУрГУ.`}
      />

      <StyledSection>
        <StyledInner>
          <StyledBio>
            <div>
              <h2>Профессиональный путь</h2>
              <p>
                Пушкарева Мария Викторовна — сотрудник ЮУрГУ, кандидат экономических наук, доцент
                кафедры «Системное программирование» Института естественных и точных наук. Опыт
                работы в разработке программного обеспечения и управлении IT-проектами — более 5
                лет.
              </p>
              <p>
                Практическая работа сочетается с академической: преподавание разработки, внедрения и
                сопровождения ПО, научная деятельность и наставничество при подготовке курсовых и
                дипломных проектов. Такой подход даёт системность, внимание к деталям и умение
                объяснять сложные решения простым языком.
              </p>
              <p>
                SMART-KVADRAT — это деятельность одного специалиста, который отвечает за результат
                лично: от формулирования требований и проектирования до разработки, контроля
                качества, защиты информации и запуска проекта.
              </p>
            </div>

            <StyledProfile>
              <div className="avatar" aria-hidden="true">
                {person.initials}
              </div>
              <h3>{person.name}</h3>
              <p className="academic">{person.academic} · ЮУрГУ</p>
              <dl>
                <div>
                  <dt>Должность</dt>
                  <dd>Доцент кафедры «Системное программирование» ИЕТН</dd>
                </div>
                <div>
                  <dt>Учёная степень</dt>
                  <dd>Кандидат экономических наук</dd>
                </div>
                <div>
                  <dt>Опыт</dt>
                  <dd>Более 5 лет в разработке и управлении IT-проектами</dd>
                </div>
                <div>
                  <dt>Контакт</dt>
                  <dd>
                    <span style={{ wordBreak: 'break-all' }}>{email}</span>
                  </dd>
                </div>
              </dl>
            </StyledProfile>
          </StyledBio>
        </StyledInner>
      </StyledSection>

      <StyledSection>
        <StyledInner>
          <p className="eyebrow">Специализация</p>
          <h2>Чем я могу помочь</h2>
          <StyledGrid>
            {specialization.map(item => (
              <StyledItem key={item.title}>
                <span className="icon">
                  <FeatureIcon name={item.icon} size={22} />
                </span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </StyledItem>
            ))}
          </StyledGrid>
        </StyledInner>
      </StyledSection>

      <CTA />
    </main>
  </Layout>
);

AboutPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default AboutPage;
